import React, { useState } from 'react';
import { X, BookOpen, Globe, Lock, Loader2, Sparkles, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { QUESTION_SUBJECTS, QUESTION_GRADES, QuestionSet } from '../../types';
import { getAuthHeaders, customFetch } from '../../services/apiService';

interface CreateQuestionSetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newSet: QuestionSet) => void;
  editingSet?: QuestionSet | null;
  onQuotaExceeded?: (message?: string) => void;
}

export const CreateQuestionSetModal: React.FC<CreateQuestionSetModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editingSet,
  onQuotaExceeded
}) => {
  // Sorted subjects from A to Z with Vietnamese collation ('Khác' at the end)
  const sortedSubjects = React.useMemo(() => {
    return [...QUESTION_SUBJECTS].sort((a, b) => {
      if (a === 'Khác') return 1;
      if (b === 'Khác') return -1;
      return a.localeCompare(b, 'vi');
    });
  }, []);

  const [name, setName] = useState(editingSet?.name || '');
  const [description, setDescription] = useState(editingSet?.description || '');
  const [subject, setSubject] = useState(editingSet?.subject || 'Toán');
  const [grade, setGrade] = useState(editingSet?.grade ? String(editingSet.grade) : '10');
  const [isPublic, setIsPublic] = useState(editingSet?.isPublic ?? false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync state if editingSet changes
  React.useEffect(() => {
    if (editingSet) {
      setName(editingSet.name);
      setDescription(editingSet.description || '');
      setSubject(editingSet.subject);
      setGrade(String(editingSet.grade));
      setIsPublic(editingSet.isPublic);
    } else {
      setName('');
      setDescription('');
      setSubject('Toán');
      setGrade('10');
      setIsPublic(false);
    }
    setError(null);
  }, [editingSet, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Vui lòng nhập tên bộ câu hỏi.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let setId = '';
      if (editingSet) {
        setId = editingSet._id || editingSet.id || '';
        if (!setId) {
          setError('Không tìm thấy ID bộ câu hỏi để cập nhật.');
          setLoading(false);
          return;
        }
      }

      const url = editingSet ? `/api/v1/questions/sets/${setId}` : '/api/v1/questions/sets';
      const method = editingSet ? 'PUT' : 'POST';

      const res = await customFetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          title: name.trim(),
          description: description.trim(),
          subject,
          grade: String(grade),
          isPublic
        })
      });

      const data = await res.json().catch(() => ({}));
      
      // Bắt lỗi 403 Forbidden khi vượt hạn mức số lượng bộ câu hỏi
      if (res.status === 403 || data.isQuotaExceeded) {
        const errorMsg = data.message || 'Tài khoản của Thầy/Cô đã đạt giới hạn số lượng bộ câu hỏi của gói hiện tại.';
        if (onQuotaExceeded) {
          onClose();
          onQuotaExceeded(errorMsg);
          return;
        }
        throw new Error(errorMsg);
      }

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Không thể lưu bộ câu hỏi.');
      }

      // Extract ID from multiple possible response formats (data.set, data.data, data)
      const rawSetData = data.set || data.data || data;
      const newSetId = rawSetData?._id || rawSetData?.id || data._id || data.id || (editingSet ? editingSet.id : undefined);

      const returnedSet: QuestionSet = {
        ...(typeof rawSetData === 'object' ? rawSetData : {}),
        id: String(newSetId || ''),
        _id: String(newSetId || ''),
        name: rawSetData?.name || rawSetData?.title || name.trim(),
        title: rawSetData?.title || rawSetData?.name || name.trim(),
        description: rawSetData?.description || description.trim(),
        subject: rawSetData?.subject || subject,
        grade: rawSetData?.grade || String(grade),
        isPublic: rawSetData?.isPublic !== undefined ? Boolean(rawSetData.isPublic) : isPublic,
        totalQuestions: rawSetData?.totalQuestions || 0
      };

      onClose();
      onSuccess(returnedSet);
    } catch (err: any) {
      setError(err.message || 'Đã xảy ra lỗi khi tạo bộ câu hỏi.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div 
        className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-slate-200 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 p-1.5 rounded-xl hover:bg-slate-100 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="flex items-center space-x-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-[#236539] flex items-center justify-center font-bold">
            <BookOpen className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">
              {editingSet ? 'Chỉnh sửa bộ câu hỏi' : 'Tạo bộ câu hỏi mới'}
            </h3>
            <p className="text-xs text-slate-500">
              Quản lý và tổ chức câu hỏi trắc nghiệm theo môn học & khối lớp
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-xl flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tên bộ câu hỏi */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Tên bộ câu hỏi <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="VD: Đề ôn tập giữa học kỳ 1 - Toán 10..."
              className="w-full bg-slate-50 border border-slate-300 rounded-xl py-2.5 px-3.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#236539]/20 focus:border-[#236539] focus:bg-white transition-all"
            />
          </div>

          {/* Mô tả bộ câu hỏi */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Mô tả bộ câu hỏi <span className="text-slate-400 font-normal normal-case">(Không bắt buộc)</span>
            </label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Nhập mô tả ngắn gọn về mục đích, đối tượng học sinh hoặc nội dung chính của bộ đề này..."
              className="w-full bg-slate-50 border border-slate-300 rounded-xl py-2.5 px-3.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#236539]/20 focus:border-[#236539] focus:bg-white transition-all resize-none"
            />
          </div>

          {/* Môn học & Khối lớp */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Môn học <span className="text-rose-500">*</span>
              </label>
              <select
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl py-2.5 px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#236539]/20 focus:border-[#236539] focus:bg-white transition-all"
              >
                {sortedSubjects.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Cấp độ / Khối lớp <span className="text-rose-500">*</span>
              </label>
              <select
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl py-2.5 px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#236539]/20 focus:border-[#236539] focus:bg-white transition-all"
              >
                {QUESTION_GRADES.map((g) => (
                  <option key={g} value={g}>
                    {isNaN(Number(g)) ? g : `Lớp ${g}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tùy chọn Công khai */}
          <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between">
            <div className="flex items-center space-x-2.5 pr-2">
              {isPublic ? (
                <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                  <Globe className="w-4 h-4" />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-lg bg-slate-200 text-slate-600 flex items-center justify-center shrink-0">
                  <Lock className="w-4 h-4" />
                </div>
              )}
              <div>
                <p className="text-xs font-bold text-slate-800">
                  {isPublic ? 'Chia sẻ lên Thư viện cộng đồng' : 'Bộ câu hỏi Riêng tư (Chỉ mình tôi)'}
                </p>
                <p className="text-[11px] text-slate-500">
                  {isPublic ? 'Các giáo viên khác có thể tìm thấy và nhân bản bộ câu hỏi này' : 'Chỉ bạn mới có thể xem và quản lý bộ câu hỏi'}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsPublic(!isPublic)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                isPublic ? 'bg-[#236539]' : 'bg-slate-300'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                  isPublic ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-2.5 pt-3 border-t border-slate-100">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={loading}
            >
              Hủy
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#236539] hover:bg-[#1b4f2c] text-white shadow-md flex items-center space-x-1.5"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              <span>{editingSet ? 'Lưu thay đổi' : 'Tạo bộ câu hỏi'}</span>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
